/**
 * CTASection Component
 *
 * Call-to-action section with headline, phone number, and contact button.
 * Features: phone icon, button hover effects, arrow icon on CTA.
 */

import {
  useContentToken,
  usePropertyField,
  EditableToken,
} from '@vibesites/runtime/react';
import type { ComponentContentSchema } from '@vibesites/runtime';
// @ts-ignore
import { PhoneIcon, ChevronRightIcon } from '@vibesites/assets';
import { formatPhone } from '../utils/format-phone';

export function CTASection() {
  const headline = useContentToken('ctasection.headline');
  const phoneToken = useContentToken('ctasection.phone');
  const trackingPhone = usePropertyField('property_call_tracking_phone') as string | null;
  // Fallback chain: editor override / custom_ppc_tracking_phone_number → property_call_tracking_phone
  const phone = (phoneToken && phoneToken.trim()) ? phoneToken : (trackingPhone || '');
  const buttonText = useContentToken('ctasection.button_text');
  const buttonUrl = useContentToken('ctasection.button_url');

  return (
    <section className="bg-primary py-10 md:py-12">
      <div className="max-w-[1200px] mx-auto px-4 md:px-6 flex flex-col items-center gap-4">
        <EditableToken path="ctasection.headline" type="text" label="Headline" as="div">
          <p className="font-heading text-lg md:text-xl font-semibold text-primary-foreground m-0 text-center">
            {headline}
          </p>
        </EditableToken>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-5 sm:gap-10 mt-1">
          {/* Phone */}
          <EditableToken path="ctasection.phone" type="phone" label="Phone Number" as="div">
            <a href={`tel:${phone?.replace(/[^+\d]/g, '')}`} className="flex items-center gap-3 text-2xl md:text-[2rem] font-medium font-accent text-primary-foreground no-underline">
              <PhoneIcon size={22} className="opacity-70" />
              {formatPhone(phone)}
            </a>
          </EditableToken>

          {/* Contact Button */}
          <EditableToken path="ctasection.button_url" type="url" label="Button URL" as="span">
            <EditableToken path="ctasection.button_text" type="text" label="Button Text" as="span">
              <a
                href={buttonUrl || '#contact'}
                className="inline-flex items-center justify-center gap-2 py-4 px-10 bg-secondary text-secondary-foreground no-underline font-semibold text-base rounded-md transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 active:scale-[0.98]"
              >
                {buttonText}
                <ChevronRightIcon size={16} />
              </a>
            </EditableToken>
          </EditableToken>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'CTASection',
  displayName: 'Call to Action',
  tokens: {
    'ctasection.headline': {
      label: 'Headline',
      type: 'text',
      defaultValue: 'Ready to find your new home?',
    },
    'ctasection.phone': {
      label: 'Phone Number',
      type: 'phone',
      defaultField: 'custom_ppc_tracking_phone_number',
      suggestedFields: ['custom_ppc_tracking_phone_number', 'property_call_tracking_phone', 'property_phone'],
    },
    'ctasection.button_text': {
      label: 'Button Text',
      type: 'text',
      defaultValue: 'Contact Us',
    },
    'ctasection.button_url': {
      label: 'Button URL',
      type: 'url',
      defaultValue: '#contact',
    },
  },
};

(CTASection as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default CTASection;
